import { useAuthStore } from "~/store/auth/auth.store";
import { SseEventType } from "~/utils/enums/sse.enums";
import { createSseParser } from "~/utils/helpers/auth/sse";
import { AuthSessionChangedError, createAuthenticatedFetch } from "~/utils/helpers/auth/transport";

export type SseEventHandler = (type: string, payload: Record<string, unknown>) => void;

const handlers = new Set<SseEventHandler>();
const DEFAULT_RECONNECT_DELAY_MS = 5_000;

let abortController: AbortController | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectDelay = DEFAULT_RECONNECT_DELAY_MS;
let shouldConnect = false;
let connecting = false;
let lastEventId: string | undefined;
let lastEventEpoch: string | undefined;

function dispatchMessage(data: string, eventName?: string): void {
  try {
    const parsed = JSON.parse(data) as {
      type?: string;
      payload?: Record<string, unknown>;
    };
    const type = parsed.type ?? eventName;
    if (!type || type === SseEventType.HEARTBEAT) return;

    for (const handler of handlers) handler(type, parsed.payload ?? {});
  } catch {
    // A malformed event does not terminate the shared stream.
  }
}

function clearReconnectTimer(): void {
  if (!reconnectTimer) return;
  clearTimeout(reconnectTimer);
  reconnectTimer = null;
}

function stopActiveConnection(owner?: AbortController): boolean {
  if (owner && abortController !== owner) return false;
  const activeController = abortController;
  if (!activeController) {
    if (!owner) connecting = false;
    return false;
  }
  abortController = null;
  connecting = false;
  activeController.abort();
  return true;
}

function scheduleReconnect(epoch: string): void {
  clearReconnectTimer();
  const authStore = useAuthStore();
  if (!shouldConnect || authStore.sessionEpoch !== epoch) return;

  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    void openConnection();
  }, reconnectDelay);
}

async function openConnection(): Promise<void> {
  if (!shouldConnect || abortController || connecting) return;

  const authStore = useAuthStore();
  if (!authStore.tokens?.accessToken || !authStore.sessionEpoch) return;

  const config = useRuntimeConfig();
  const epoch = authStore.sessionEpoch;
  if (lastEventEpoch !== epoch) {
    lastEventEpoch = epoch;
    lastEventId = undefined;
    reconnectDelay = DEFAULT_RECONNECT_DELAY_MS;
  }
  const controller = new AbortController();
  abortController = controller;
  connecting = true;

  try {
    const response = await createAuthenticatedFetch(authStore)(`${config.public.ORDERS_URL}/sse/stream`, {
      method: "GET",
      headers: {
        Accept: "text/event-stream",
        ...(lastEventId !== undefined ? { "Last-Event-ID": lastEventId } : {}),
      },
      signal: controller.signal,
    });
    if (authStore.sessionEpoch !== epoch) throw new AuthSessionChangedError();
    if (abortController === controller) connecting = false;

    if (response.status === 204) {
      if (stopActiveConnection(controller)) shouldConnect = false;
      return;
    }

    if (!response.ok) throw new Error(`SSE connection failed (HTTP ${response.status})`);
    if (!response.body) throw new Error("SSE connection returned no body");

    const parser = createSseParser({
      onEvent: (event) => {
        if (authStore.sessionEpoch !== epoch || abortController !== controller) throw new AuthSessionChangedError();
        if (event.id !== undefined) lastEventId = event.id || undefined;
        dispatchMessage(event.data, event.event);
      },
      onRetry: (milliseconds) => {
        reconnectDelay = milliseconds;
      },
    });
    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    while (true) {
      const { done, value } = await reader.read();
      if (authStore.sessionEpoch !== epoch) throw new AuthSessionChangedError();
      if (done) break;
      parser.feed(decoder.decode(value, { stream: true }));
    }
    parser.feed(decoder.decode());
    parser.finish();
    if (stopActiveConnection(controller)) scheduleReconnect(epoch);
  } catch (error) {
    const sessionChanged = error instanceof AuthSessionChangedError || authStore.sessionEpoch !== epoch;
    const intentionallyClosed = controller.signal.aborted && !sessionChanged;
    const ownedConnection = stopActiveConnection(controller);
    if (ownedConnection && !sessionChanged && !intentionallyClosed) scheduleReconnect(epoch);
  }
}

// useSseStream() → single shared authenticated SSE connection, multiple handlers
export const useSseStream = () => {
  const onBeforeUnload = () => disconnect();

  const connect = () => {
    shouldConnect = true;
    window.addEventListener("beforeunload", onBeforeUnload);
    void openConnection();
  };

  const disconnect = () => {
    shouldConnect = false;
    clearReconnectTimer();
    stopActiveConnection();
    window.removeEventListener("beforeunload", onBeforeUnload);
  };

  const onEvent = (handler: SseEventHandler) => {
    handlers.add(handler);
    return () => handlers.delete(handler);
  };

  return { connect, disconnect, onEvent };
};
